<script setup lang="ts">
import type { FieldSize } from '@/components/fields/KTextField.vue';

import { ref, useTemplateRef, watch } from 'vue';
import { VueDatePicker } from '@vuepic/vue-datepicker';
import { format, getDaysInMonth } from 'date-fns';

import FallbackMdiIconCmn from '@/components/cmns/FallbackMdiIconCmn.vue';
import KTextField from '@/components/fields/KTextField.vue';
import usePickerField from '@/composables/usePickerField';

import datepickerStyles from '@/styles/datepicker.module.scss';

export interface MonthPickerFieldValue {
  month: number;
  year: number;
}

export interface Props {
  modelValue?: string | null;
  label?: string;
  loading?: boolean;
  disabled?: boolean;
  hideDetails?: boolean;
  rules?: object;
  size?: FieldSize;
  autoApply?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  label: 'Date',
  loading: false,
  disabled: false,
  hideDetails: false,
  rules: () => {
    return [];
  },
  size: undefined,
  autoApply: false,
});
const emit = defineEmits<{
  'update:modelValue': [value: (typeof props)['modelValue']];
}>();

const modelFormatPattern = 'yyyy-MM-dd';
const fieldFormatPartPattern = 'dd MMM yyyy';
const fieldFormatMonthPattern = 'MMMM yyyy';

const fieldValue = ref<MonthPickerFieldValue | null>();
const datepickerEl = useTemplateRef('datepicker');
const root = ref<HTMLElement | null>(null);

function convertStringDateToMonthPickerFieldValue(
  value: string,
): MonthPickerFieldValue {
  const date = new Date(value);
  const monthIndex = date.getMonth();
  const fullYear = date.getFullYear();
  return {
    month: monthIndex,
    year: fullYear,
  };
}

function convertMonthPickerFieldValueToStringDate(
  value: MonthPickerFieldValue,
): string {
  const date = new Date(value.year, value.month);
  return format(date, modelFormatPattern);
}

watch(
  () => props.modelValue,
  (newModelValue) => {
    if (typeof newModelValue === 'string') {
      fieldValue.value =
        convertStringDateToMonthPickerFieldValue(newModelValue);
    } else {
      fieldValue.value = newModelValue;
    }
  },
  { immediate: true },
);

watch(fieldValue, (newFieldValue) => {
  let value;
  if (newFieldValue === undefined || newFieldValue === null) {
    value = newFieldValue;
  } else {
    value = convertMonthPickerFieldValueToStringDate(newFieldValue);
  }
  emit('update:modelValue', value);
});

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function formatMonthPicker(date: Date | string): string {
  if (typeof date === 'string') {
    return date;
  } else {
    const monthIndex = date.getMonth();
    const fullYear = date.getFullYear();
    const maxDay = getDaysInMonth(date);
    const fromDate = new Date(fullYear, monthIndex);
    const toDate = new Date(fullYear, monthIndex, maxDay);
    return (
      format(fromDate, fieldFormatPartPattern) +
      '–' +
      format(toDate, fieldFormatPartPattern)
    );
  }
}

const {
  pickerAttrs,
  handleAllEnterKeydown,
  // handleFocus,
  // handleClick,
  handleAllEscKeydown,
  handleAppendIconClick,
  handleAllSpaceKeydown,
  handleDownKeydown,
} = usePickerField(root, datepickerEl);
</script>

<template>
  <div ref="root" data-testid="monthPickerField">
    <VueDatePicker
      v-bind="pickerAttrs"
      ref="datepicker"
      v-model="fieldValue"
      :formats="{ input: fieldFormatMonthPattern }"
      :auto-apply="props.autoApply"
      :class="[
        $style.monthPickerField,
        {
          [$style.datepickerHideDetails]: props.hideDetails,
        },
      ]"
      :ui="{
        menu: datepickerStyles.dpCustomMenu,
        calendar: datepickerStyles.dpCustomCalendar,
      }"
      :text-input="{
        enterSubmit: true,
        tabSubmit: true,
        openMenu: !props.disabled ? 'open' : false,
      }"
      month-picker
      @keydown.enter="handleAllEnterKeydown"
      @keydown.space="handleAllSpaceKeydown"
      @keydown.esc="handleAllEscKeydown"
    >
      <template #arrow-left>
        <FallbackMdiIconCmn icon="mdi-chevron-left" />
      </template>
      <template #arrow-right>
        <FallbackMdiIconCmn icon="mdi-chevron-right" />
      </template>
      <template #arrow-up>
        <FallbackMdiIconCmn icon="mdi-chevron-up" />
      </template>
      <template #arrow-down>
        <FallbackMdiIconCmn icon="mdi-chevron-down" />
      </template>
      <template #calendar-icon>
        <FallbackMdiIconCmn icon="mdi-calendar-blank-outline" />
      </template>
      <template
        #dp-input="{
          value,
          onInput,
          onEnter,
          onTab,
          onClear,
          onBlur,
          onKeypress,
          onPaste,
          isMenuOpen,
        }"
      >
        <KTextField
          append-inner-icon="mdi-calendar-blank-outline"
          :label="props.label"
          clearable
          :model-value="value"
          :loading="props.loading"
          :disabled="props.disabled"
          :hide-details="props.hideDetails"
          :rules="props.rules"
          :size="props.size"
          :class="$style.field"
          readonly
          @input="onInput"
          @keydown.tab="onTab"
          @blur="onBlur"
          @keydown.down.prevent="handleDownKeydown($event, isMenuOpen)"
          @paste="onPaste"
          @click:clear="onClear"
          @click:append-inner="handleAppendIconClick($event, isMenuOpen)"
        />
      </template>
    </VueDatePicker>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/variables';

.monthPickerField {
  padding-bottom: 22px;
}
.datepickerHideDetails {
  padding-bottom: 0;
}

.field {
  :global {
    .v-field {
      cursor: pointer;
    }
    input.v-field__input {
      padding-right: 0;
      cursor: pointer;
    }
    .v-input__details {
      position: absolute;
      bottom: -22px;
    }
  }
}
</style>
